'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useCallback, useEffect, useRef, useState } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'

// walrus
import { accessibilityLabel } from '../../kit/_support'
import Box from '../../kit/box'
import { Logomark } from '../../kit/logo'
import Pressable from '../../kit/pressable'
import Text from '../../kit/text'

// lib
import SideNav from './side-nav'

/* -----------------------------------------------------------------------------
 * AppShell
 *
 * The labelled 232px sidebar from the clinical dashboard design. Every screen
 * is on it now, so the legacy icon rail it replaced is gone.
 *
 * The sidebar collapses to an icon rail. Pressing the logo toggles it, and the
 * transition runs in three steps so nothing is seen mid-swap: the nav group
 * fades out, the rail resizes while it is invisible, then the other version of
 * the group fades in. The logo lockup is not resized, only clipped by the rail
 * narrowing over it, which is what leaves the bare W behind.
 * -------------------------------------------------------------------------- */

const COMPACT_WIDTH = 72
const EXPANDED_WIDTH = 232
const FADE_MS = 120
const RESIZE_MS = 200
const TABLET_MIN_WIDTH = 768

// The logo lockup gets its own clip, narrower than the rail: stopping at the
// rail edge would still leave a sliver of the wordmark showing. The mark also
// slides right by the difference between where it sits when expanded and where
// the rail's centre line is, so the W lines up with the icons under it.
const LOGO_SIZE = 28
const LOGO_INSET = 20 // keep in step with the row's pl below
const LOGO_COMPACT_SHIFT = (COMPACT_WIDTH - LOGO_SIZE) / 2 - LOGO_INSET
const LOGO_COMPACT_WIDTH = LOGO_INSET + LOGO_COMPACT_SHIFT + LOGO_SIZE

const resizeTransition = (property: string) => ({
  transitionDuration: `${RESIZE_MS}ms`,
  transitionProperty: property,
  transitionTimingFunction: 'ease-in-out'
})

export type AppShellProps = {
  activePage: string
  children: React.ReactNode
  defaultCompact?: boolean
  /** hand the content area over whole, for screens that lay out their own
   * full-height panels instead of scrolling as a page */
  fullBleed?: boolean
  label?: string
  onActivePagePress?: () => void
  onBeforeNavChange?: () => void
}

function AppShell({
  activePage,
  children,
  defaultCompact = false,
  fullBleed = false,
  label = 'App',
  onActivePagePress,
  onBeforeNavChange
}: AppShellProps) {
  const isRail = useMediaQuery(`(min-width:${TABLET_MIN_WIDTH}px)`, {
    noSsr: true
  })

  const [isCompact, setIsCompact] = useState(defaultCompact)
  const [isNavVisible, setIsNavVisible] = useState(true)
  const isAnimating = useRef(false)
  const timers = useRef<number[]>([])

  useEffect(() => () => timers.current.forEach(window.clearTimeout), [])

  const after = (ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms))
  }

  const setCompact = useCallback(
    (next: boolean, onSettled?: () => void) => {
      if (isAnimating.current || next === isCompact) return onSettled?.()

      isAnimating.current = true
      onBeforeNavChange?.()

      // fade out, resize while hidden, fade the other version in
      setIsNavVisible(false)
      after(FADE_MS, () => {
        setIsCompact(next)
        after(RESIZE_MS, () => {
          setIsNavVisible(true)
          after(FADE_MS, () => {
            isAnimating.current = false
            onSettled?.()
          })
        })
      })
    },
    [isCompact, onBeforeNavChange]
  )

  // pressing the entry for the page you are already on collapses the rail
  // first, then hands over — chat opens its member list into the space
  const handleActivePagePress = useCallback(() => {
    if (!onActivePagePress) return
    setCompact(true, onActivePagePress)
  }, [onActivePagePress, setCompact])

  return (
    <Box
      bg='proCanvas'
      flexDirection={{ tablet: 'row' }}
      height='100%'
      {...accessibilityLabel(label)}
    >
      <Box
        bg='proSurface'
        borderColor='proBorder'
        borderRightWidth={{ tablet: 1 }}
        borderStyle='solid'
        flex='0 0 auto'
        order={{ tablet: 1 }}
        overflowX='hidden'
        overflowY='hidden'
        py='20'
        style={
          isRail
            ? {
                width: isCompact ? COMPACT_WIDTH : EXPANDED_WIDTH,
                ...resizeTransition('width')
              }
            : undefined
        }
        width={{ tablet: EXPANDED_WIDTH }}
      >
        <Pressable
          accessibilityLabel={
            isCompact ? 'Expand navigation' : 'Collapse navigation'
          }
          flex='0 0 auto'
          onPress={() => setCompact(!isCompact)}
          pb='24'
        >
          {/* the wrapper clips, the row inside keeps its full width and slides
           * — the lockup is never resized, only cropped and re-centred */}
          <Box
            overflowX='hidden'
            overflowY='hidden'
            style={
              isRail
                ? {
                    width: isCompact ? LOGO_COMPACT_WIDTH : EXPANDED_WIDTH,
                    ...resizeTransition('width')
                  }
                : undefined
            }
            width={{ tablet: EXPANDED_WIDTH }}
          >
            <Box
              alignItems='center'
              flex='0 0 auto'
              flexDirection='row'
              gap='12'
              pl='20'
              style={
                isRail
                  ? {
                      transform: [
                        { translateX: isCompact ? LOGO_COMPACT_SHIFT : 0 }
                      ],
                      ...resizeTransition('transform')
                    }
                  : undefined
              }
              width={EXPANDED_WIDTH}
            >
              <Logomark
                fill='proAccent'
                flex='0 0 auto'
                height={LOGO_SIZE}
                width={LOGO_SIZE}
              />
              <Text
                flex='0 0 auto'
                font='pro.text.16.semibold'
                numberOfLines={1}
                textColor='proInk'
              >
                Walrus Health
              </Text>
            </Box>
          </Box>
        </Pressable>

        <Box
          style={{
            opacity: isNavVisible ? 1 : 0,
            transitionDuration: `${FADE_MS}ms`,
            transitionProperty: 'opacity',
            transitionTimingFunction: 'ease-out'
          }}
        >
          <SideNav
            activePage={activePage}
            compact={isRail && isCompact}
            onActivePagePress={onActivePagePress && handleActivePagePress}
          />
        </Box>
      </Box>

      {fullBleed ? (
        <Box
          bg='proCanvas'
          flex='1 1 auto'
          height='100vh'
          order={{ tablet: 2 }}
          overflowX='hidden'
          overflowY='hidden'
        >
          {children}
        </Box>
      ) : (
        <Box
          bg='proCanvas'
          flex='1 1 auto'
          height='100vh'
          order={{ tablet: 2 }}
          overflowY='scroll'
          pb='64'
          px={{ mobile: '16', tablet: '32' }}
          pt='24'
        >
          <Box maxWidth={1440} width='100%'>
            {children}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default AppShell
